<script setup lang="ts">
import { ref } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useStore } from 'vuex'
import { toTypedSchema } from '@vee-validate/zod'
import { useAuth } from '@/composables/useAuth'
import { resetPasswordSchema, getSafeRedirectPath, type ResetPasswordFormData } from '@/utils/auth/validation'
import { toast } from '@/components/ui/toast/use-toast'
import { handleAuthError } from '@/utils/auth/errorMapping'
import EmailAuthForm from './components/EmailAuthForm.vue'
import PhoneAuthForm from './components/PhoneAuthForm.vue'
import { Form, FormField, FormItem, FormControl, FormLabel, FormMessage } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Mail, Phone } from 'lucide-vue-next'

const props = withDefaults(defineProps<{
  heading?: string
  subheading?: string
  localToggle?: boolean
}>(), {
  heading: 'Welcome back',
  subheading: 'Sign in to your account to continue',
  localToggle: false,
})

const emit = defineEmits<{ 'toggle-to-signup': [] }>()

const router = useRouter()
const route = useRoute()
const store = useStore()
const { signInWithGoogle, sendPasswordReset, isLoading, error, clearError } = useAuth()

const googlePending = ref(false)
const prefillEmail = (route.query.email as string) || ''
const showEmailForm = ref(!!prefillEmail || route.query.method === 'email')

const handleGoogleSignIn = async () => {
  googlePending.value = true
  const token = await signInWithGoogle()
  googlePending.value = false
  if (token) {
    handleAuthSuccess(token)
  } else if (error.value) {
    toast({ title: 'Google sign-in failed', description: error.value, variant: 'destructive' })
  }
}

type AuthMode = 'signin' | 'reset'
const authMode = ref<AuthMode>('signin')

const showPhoneForm = ref(false)

const switchMode = (mode: AuthMode) => {
  authMode.value = mode
  showEmailForm.value = false
  showPhoneForm.value = false
  clearError()
}

const toggleEmailForm = () => {
  showEmailForm.value = !showEmailForm.value
  showPhoneForm.value = false
  clearError()
}

const togglePhoneForm = () => {
  showPhoneForm.value = !showPhoneForm.value
  showEmailForm.value = false
  clearError()
}

const handleAuthSuccess = async (token: string) => {
  console.log('[SignInPage] Authentication successful, exchanging token with backend...')

  try {
    const result = await store.dispatch('user/handleAuthentication', {
      firebaseToken: token
    })

    if (result.success) {
      console.log('[SignInPage] Backend authentication successful')
      const redirectPath = getSafeRedirectPath(route.query.redirect)
      router.push(redirectPath || { name: 'AuthOverview' })
      return
    } else {
      if (result.error === 'USER_NOT_REGISTERED') {
        const redirectPath = getSafeRedirectPath(route.query.redirect)
        if (redirectPath) {
          console.log('[SignInPage] User not registered, redirecting to invite flow:', redirectPath)
          router.push(redirectPath)
          return
        }
        console.log('[SignInPage] User not registered, letting App.vue handle routing')
        return
      }

      if (result.error === 'EMAIL_NOT_VERIFIED') {
        console.log('[SignInPage] Email not verified')
        toast({
          title: 'Email Not Verified',
          description: 'Please check your email and click the verification link before signing in.',
          variant: 'default'
        })
        return
      }

      console.error('[SignInPage] Backend authentication failed:', result.reason)
      const errorMessage = handleAuthError(result.reason || new Error('Backend authentication failed'), 'backend')
      toast({ title: 'Authentication failed', description: errorMessage, variant: 'destructive' })
    }
  } catch (err: any) {
    console.error('[SignInPage] Authentication error:', err)

    if (err?.response?.data?.detail?.error === 'USER_NOT_REGISTERED') {
      const redirectPath = getSafeRedirectPath(route.query.redirect)
      if (redirectPath) {
        console.log('[SignInPage] User not registered (from error), redirecting to invite flow:', redirectPath)
        router.push(redirectPath)
        return
      }
      console.log('[SignInPage] User not registered (from error), letting App.vue handle routing')
      return
    }

    if (err?.response?.data?.detail?.error === 'EMAIL_NOT_VERIFIED') {
      console.log('[SignInPage] Email not verified (from error)')
      toast({
        title: 'Email Not Verified',
        description: err?.response?.data?.detail?.message || 'Please check your email and click the verification link before signing in.',
        variant: 'default'
      })
      return
    }

    const errorMessage = handleAuthError(err, 'backend')
    toast({ title: 'Authentication failed', description: errorMessage, variant: 'destructive' })
  }
}

const showAuthError = (message: string) => {
  toast({ title: 'Sign in failed', description: message, variant: 'destructive' })
}

const onPasswordReset = async (values: Record<string, any>) => {
  const typedValues = values as ResetPasswordFormData
  const success = await sendPasswordReset(typedValues.email)

  if (success) {
    toast({
      title: 'Password reset email sent',
      description: 'Check your email for instructions to reset your password'
    })
    switchMode('signin')
  } else {
    toast({
      title: 'Failed to send reset email',
      description: 'Please try again or contact support',
      variant: 'destructive'
    })
  }
}
</script>

<template>
  <div class="space-y-8 py-6">
    <!-- Header -->
    <div class="text-center space-y-2">
      <h1 class="text-3xl font-bold tracking-tight text-foreground">
        <span v-if="authMode === 'signin'">{{ props.heading }}</span>
        <span v-else>Reset password</span>
      </h1>
      <p class="text-sm text-muted-foreground">
        <span v-if="authMode === 'signin'">{{ props.subheading }}</span>
        <span v-else>Enter your email to receive a password reset link</span>
      </p>
    </div>

    <!-- Sign In mode -->
    <div v-if="authMode !== 'reset'">
      <div v-if="!showEmailForm && !showPhoneForm" class="space-y-3 py-4 px-6">
        <p v-if="googlePending" class="text-center text-sm text-green-600 animate-pulse mb-5">
          Waiting for Google sign-in&hellip;
        </p>

        <Button variant="outline" class="w-full !justify-start pl-[15%]" :disabled="isLoading" @click="handleGoogleSignIn">
          <span class="w-10 flex justify-center shrink-0">
            <svg class="w-5 h-5" viewBox="0 0 24 24">
              <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
          </span>
          Continue with Google
        </Button>

        <Button variant="outline" class="w-full !justify-start pl-[15%]" :disabled="isLoading" @click="toggleEmailForm">
          <span class="w-10 flex justify-center shrink-0"><Mail class="w-5 h-5" /></span>
          Continue with Email
        </Button>

        <Button variant="outline" class="w-full !justify-start pl-[15%]" :disabled="isLoading" @click="togglePhoneForm">
          <span class="w-10 flex justify-center shrink-0"><Phone class="w-5 h-5" /></span>
          Continue with Phone
        </Button>
      </div>

      <div v-else-if="showEmailForm">
        <div class="text-center mb-6">
          <button @click="toggleEmailForm" class="text-sm text-muted-foreground hover:text-foreground hover:underline inline-flex items-center gap-1">
            <span>←</span><span>Back to all sign in options</span>
          </button>
        </div>
        <EmailAuthForm
          :initial-email="prefillEmail"
          @success="handleAuthSuccess"
          @error="showAuthError"
          @forgot-password="switchMode('reset')"
        />
      </div>

      <div v-else-if="showPhoneForm">
        <div class="text-center mb-6">
          <button @click="togglePhoneForm" class="text-sm text-muted-foreground hover:text-foreground hover:underline inline-flex items-center gap-1">
            <span>←</span><span>Back to all sign in options</span>
          </button>
        </div>
        <PhoneAuthForm @success="handleAuthSuccess" @error="showAuthError" />
      </div>
    </div>

    <!-- Password Reset mode -->
    <div v-else>
      <Form
        :validation-schema="toTypedSchema(resetPasswordSchema)"
        @submit="onPasswordReset"
        class="space-y-4"
        :validate-on-blur="true"
        :validate-on-change="false"
        :validate-on-input="false"
        :validate-on-model-update="false"
      >
        <FormField name="email" v-slot="{ componentField }">
          <FormItem>
            <FormLabel>Email</FormLabel>
            <FormControl>
              <Input type="email" placeholder="your@email.com" autocomplete="email" v-bind="componentField" />
            </FormControl>
            <FormMessage />
          </FormItem>
        </FormField>
        <Button type="submit" :disabled="isLoading" class="w-full">
          <span v-if="isLoading">Sending...</span>
          <span v-else>Send reset link</span>
        </Button>
      </Form>

      <div class="text-center pt-4">
        <button @click="switchMode('signin'); showEmailForm = false" class="text-sm text-primary hover:text-primary/80 hover:underline">
          ← Back to sign in
        </button>
      </div>
    </div>

    <!-- Don't have an account -->
    <div v-if="authMode !== 'reset' && !props.localToggle" class="text-center text-sm">
      <span class="text-gray-600">Don't have an account? </span>
      <Button
        variant="link"
        class="p-0 h-auto"
        @click="props.localToggle ? emit('toggle-to-signup') : router.push(route.query.redirect ? { path: '/signup', query: { redirect: route.query.redirect } } : '/signup')"
      >
        Sign up
      </Button>
    </div>
  </div>
</template>
